import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  Rating
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import AIMemoryForm from '../../../components/admin/ai-orchestrator/AIMemoryForm';
import { useSnackbar } from 'notistack';
import aiOrchestratorService from '../../../services/aiOrchestratorService';

const AIOrchestratorMemory = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [memories, setMemories] = useState([]);
  const [agents, setAgents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMemories = async () => {
    try {
      const response = await aiOrchestratorService.getAgentMemory({ orchestratorId: id });
      setMemories(response.data || []);
      setLoading(false);
    } catch (error) {
      enqueueSnackbar('Failed to fetch memories', { variant: 'error' });
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await aiOrchestratorService.getOrchestrator(id);
      setAgents(response.data.agents || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch agents', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchMemories();
    fetchAgents();
  }, [id]);

  const handleSubmit = async (formData) => {
    try {
      if (selectedMemory) {
        await aiOrchestratorService.updateMemory(id, selectedMemory._id, formData);
        enqueueSnackbar('Memory updated successfully', { variant: 'success' });
      } else {
        await aiOrchestratorService.createMemory(id, formData);
        enqueueSnackbar('Memory created successfully', { variant: 'success' });
      }
      setOpenDialog(false);
      setSelectedMemory(null);
      fetchMemories();
    } catch (error) {
      enqueueSnackbar(error.message || 'Operation failed', { variant: 'error' });
    }
  };

  const handleDelete = async (memoryId) => {
    if (window.confirm('Are you sure you want to delete this memory?')) {
      try {
        await aiOrchestratorService.deleteMemory(id, memoryId);
        enqueueSnackbar('Memory deleted successfully', { variant: 'success' });
        fetchMemories();
      } catch (error) {
        enqueueSnackbar('Failed to delete memory', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">AI Memory</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedMemory(null);
            setOpenDialog(true);
          }}
        >
          Add Memory
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Task Type</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Relevance</TableCell>
              <TableCell>Quality</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {memories.map((memory) => (
              <TableRow key={memory._id}>
                <TableCell>
                  {agents.find(a => a._id === memory.agentId)?.name || memory.agentId}
                </TableCell>
                <TableCell>{memory.taskType}</TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {memory.content}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Rating
                    value={memory.metadata.relevance * 5}
                    readOnly
                    precision={0.5}
                    max={5}
                  />
                </TableCell>
                <TableCell>
                  <Rating
                    value={memory.metadata.quality * 5}
                    readOnly
                    precision={0.5}
                    max={5}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      setSelectedMemory(memory);
                      setOpenDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(memory._id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedMemory(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <AIMemoryForm
          initialData={selectedMemory}
          onSubmit={handleSubmit}
          isEditMode={!!selectedMemory}
          agents={agents}
        />
      </Dialog>
    </Box>
  );
};

export default AIOrchestratorMemory; 